/** Shared rules for “text uploads” (session BFF, browser-hosted, drag/drop). */

export const UPLOAD_TEXT_EXTENSIONS = new Set(
  [
    'txt',
    'md',
    'markdown',
    'json',
    'jsonc',
    'csv',
    'ts',
    'tsx',
    'mts',
    'cts',
    'js',
    'jsx',
    'mjs',
    'cjs',
    'css',
    'scss',
    'sass',
    'less',
    'html',
    'htm',
    'xml',
    'svg',
    'yml',
    'yaml',
    'toml',
    'ini',
    'cfg',
    'conf',
    'env',
    'sh',
    'bash',
    'zsh',
    'fish',
    'ps1',
    'bat',
    'cmd',
    'sql',
    'graphql',
    'gql',
    'rs',
    'go',
    'py',
    'rb',
    'php',
    'java',
    'kt',
    'kts',
    'swift',
    'c',
    'h',
    'cc',
    'cpp',
    'hpp',
    'cs',
    'fs',
    'fsx',
    'vue',
    'svelte',
    'astro',
    'r',
    'R',
    'tex',
    'log',
    'diff',
    'patch',
    'lock',
    'gitignore',
    'dockerignore',
    'pdf',
  ].map((s) => s.toLowerCase()),
)

function fileExtension(fileName: string): string {
  const base = fileName.replace(/^.*[/\\]/, '')
  const dot = base.lastIndexOf('.')
  if (dot <= 0 || dot === base.length - 1) return ''
  return base.slice(dot + 1).toLowerCase()
}

export function sanitizeUploadFileName(fileName: string): string {
  const base = fileName.replace(/^.*[/\\]/, '').trim()
  return base || 'upload'
}

export function isAcceptableTextUpload(fileName: string, mimeType: string): boolean {
  const ext = fileExtension(fileName)
  if (ext && UPLOAD_TEXT_EXTENSIONS.has(ext)) return true
  const m = mimeType.toLowerCase().trim()
  if (!m || m === 'application/octet-stream') return Boolean(ext && UPLOAD_TEXT_EXTENSIONS.has(ext))
  if (m.startsWith('text/')) return true
  if (
    m === 'application/json' ||
    m === 'application/xml' ||
    m === 'application/javascript' ||
    m === 'application/x-javascript' ||
    m === 'application/ecmascript' ||
    m === 'application/graphql' ||
    m === 'application/sql' ||
    m === 'application/x-yaml' ||
    m === 'application/yaml' ||
    m === 'application/toml' ||
    m === 'application/x-sh' ||
    m === 'application/x-httpd-php' ||
    m === 'application/x-envoy' ||
    m === 'application/vnd.coffeescript'
  ) {
    return true
  }
  return false
}

/** `.pdf` extension or `application/pdf` MIME (handled by PDF text extraction, not as raw UTF-8). */
export function isPdfUpload(fileName: string, mimeType: string): boolean {
  const ext = fileExtension(fileName)
  if (ext === 'pdf') return true
  const m = mimeType.toLowerCase().trim()
  return m === 'application/pdf'
}

/** `.xlsx` / `.xls` or spreadsheet MIME (handled by Excel text extraction, not as raw UTF-8). */
export function isExcelUpload(fileName: string, mimeType: string): boolean {
  const ext = fileExtension(fileName)
  if (ext === 'xlsx' || ext === 'xls') return true
  const m = mimeType.toLowerCase().trim()
  return (
    m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    m === 'application/vnd.ms-excel'
  )
}

export function excelMimeTypeForUpload(fileName: string): string {
  const ext = fileExtension(fileName)
  return ext === 'xls'
    ? 'application/vnd.ms-excel'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

/** `.docx` / `.doc` or Word MIME (handled by Word text extraction, not as raw UTF-8). */
export function isWordUpload(fileName: string, mimeType: string): boolean {
  const ext = fileExtension(fileName)
  if (ext === 'docx' || ext === 'doc') return true
  const m = mimeType.toLowerCase().trim()
  return (
    m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    m === 'application/msword'
  )
}

export function wordMimeTypeForUpload(fileName: string): string {
  const ext = fileExtension(fileName)
  return ext === 'doc'
    ? 'application/msword'
    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
}

/** `.pptx` or PowerPoint MIME (handled by PPTX text extraction, not as raw UTF-8). Only OOXML (.pptx) is supported; legacy .ppt binary format is not. */
export function isPptxUpload(fileName: string, mimeType: string): boolean {
  const ext = fileExtension(fileName)
  if (ext === 'pptx') return true
  const m = mimeType.toLowerCase().trim()
  return m === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
}

export function pptxMimeTypeForUpload(_fileName: string): string {
  return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
}

/** Reject obvious binary types even if extension is wrong. */
export function isBlockedBinaryMime(mimeType: string): boolean {
  const m = mimeType.toLowerCase().trim()
  if (!m) return false
  if (m.startsWith('image/')) return true
  if (m.startsWith('audio/')) return true
  if (m.startsWith('video/')) return true
  if (m.includes('zip') || m.includes('tar') || m.includes('gzip') || m.includes('rar') || m.includes('7z')) return true
  return false
}

export function documentTitleFromUploadFileName(fileName: string): string {
  const base = sanitizeUploadFileName(fileName)
  return base.replace(/\.[^/.]+$/, '') || base || 'Untitled'
}

export function utf8TextLooksCorrupted(text: string): boolean {
  if (!text.includes('\uFFFD')) return false
  const sample = text.slice(0, 16_384)
  const bad = (sample.match(/\uFFFD/g) ?? []).length
  return bad > Math.max(2, Math.floor(sample.length / 10_000))
}
